import { secondStorage, secondDatabase } from "../../../config/firebase";

/**
 * Uploads an array of image objects to Firebase using secondStorage
 * and stores metadata in secondDatabase.
 *
 * @param {Array} images - Array of image objects, each with `id` and `file` properties.
 * @param {string} basePath - Base path in Firebase Storage to save images.
 * @param {function} onProgress - Optional callback for progress updates.
 * @returns {Promise<Array>} - Returns a promise that resolves with an array of URLs.
 */
export const uploadImagesToFirebase = async (
  images,
  basePath = "images",
  onProgress = null
) => {
  if (!Array.isArray(images)) {
    throw new Error("Input must be an array of image objects.");
  }

  // Skip processing if no images
  if (images.length === 0) {
    return [];
  }

  // For smaller batches, use optimized settings
  const useCompression = images.length > 10;

  // Pre-process images in batches for better performance
  // Optimize small images first for quick wins
  const smallImages = images.filter(
    (img) => img.file && img.file.size < 1024 * 1024
  );
  const largeImages = images.filter(
    (img) => img.file && img.file.size >= 1024 * 1024
  );

  // Process small images first to show progress quickly
  const batchedImages = [...smallImages, ...largeImages];

  // Update progress immediately for UI feedback
  if (onProgress) onProgress(5);

  // Create upload promises for all images at once for parallel processing
  const uploadPromises = batchedImages.map(async (image, index) => {
    try {
      // Update progress periodically
      if (onProgress) {
        const progressPercent = 5 + Math.floor((index / images.length) * 30);
        onProgress(progressPercent);
      }

      const file = image.file;
      // Ensure unique filenames by using timestamp + random string
      const uniqueId = Date.now() + Math.random().toString(36).substring(2, 10);
      const uniqueFileName = `${basePath}/image_${uniqueId}_${index}`;
      const fileRef = secondStorage.ref(uniqueFileName);

      // Upload file to Firebase Storage
      await fileRef.put(file);

      // Get the download URL for the uploaded file
      const downloadURL = await fileRef.getDownloadURL();

      // Save metadata in secondDatabase (optional) - do this after all uploads to avoid slowing down
      setTimeout(() => {
        secondDatabase
          .ref(`${basePath}/metadata/${uniqueId}`)
          .set({ id: image.id, url: downloadURL })
          .catch((err) => console.log("Metadata storage error:", err));
      }, 0);

      // Update progress for this image
      if (onProgress) {
        const progressPercent = 35 + Math.floor((index / images.length) * 60);
        onProgress(progressPercent);
      }

      return downloadURL;
    } catch (error) {
      console.error("Error uploading image:", error);
      // Return null for failed uploads instead of throwing error
      // This allows other uploads to continue if one fails
      return null;
    }
  });

  try {
    // Wait for all uploads to complete in parallel
    const uploadedURLs = await Promise.all(uploadPromises);

    // Filter out failed uploads
    const successfulUploads = uploadedURLs.filter(Boolean);

    // Signal completion
    if (onProgress) onProgress(100);

    return successfulUploads;
  } catch (error) {
    console.error("Error in batch upload:", error);
    // In case of error, return what we have so far
    if (onProgress) onProgress(100);
    return [];
  }
};
